/**
 * useAIChatStream — AG-UI hook for the Forecast AI chat panel.
 *
 * Uses @ag-ui/client HttpAgent for the full AG-UI event lifecycle.
 * The gateway URL comes from gen-ai/aiGateway, shared with every other gateway
 * call. The hook owns all message + streaming state.
 */
import {useState, useCallback, useRef, useEffect, useLayoutEffect} from 'react';
import type {Dispatch, MutableRefObject, SetStateAction} from 'react';
import {commitLocalUpdate} from 'react-relay';
import {ConnectionHandler} from 'relay-runtime';
import {HttpAgent} from '@ag-ui/client';
import type {HttpAgentConfig, RunAgentInput} from '@ag-ui/client';
import {v4 as uuid} from 'uuid';
import type {ActionPlan, ActionResult, EntityType, EntitySubtype} from './actionCardTypes';
import type {EditedValue} from './editedValues';
import {parseActionPlan} from './actionCardTypes';
import type {ActionCardState, LegacyActionFields} from './actionMessages';
import {
	actionStateOf,
	applyActionResult,
	attachActionPlan,
	dismissAction,
	findActionPlan,
	indexOfActionMessage,
	isTransportFailure,
	normalizePersistedMessage,
} from './actionMessages';
import RelayEnvironment from '../../RelayEnvironment';
import {AI_GATEWAY_URL} from '../gen-ai/aiGateway';
import {notifyAiCreditsExhausted} from '../gen-ai/aiCreditsSignal';
import {
	NOVA_RESPONSE_TYPE,
	trackNovaActionProposed,
	trackNovaChatReplyFailed,
	trackNovaChatReplyReceived,
} from '../../tracking/amplitude/novaEvents';
import {claimNovaSession, getNovaIdentity, setNovaThreadIdSource} from '../../tracking/amplitude/novaTrackingSession';

// ── Correlation id ───────────────────────────────────────────────────────
//
// The gateway mints an x-forecast-correlation-id per request and echoes it
// as a response header (forecast-ai-agents gateway/correlation.py), exposed
// cross-origin via CORS expose_headers. Surfacing it on a failed turn gives
// support a reference they can paste straight into Datadog.

const CORRELATION_ID_HEADER = 'x-forecast-correlation-id';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Pulls the gateway's correlation id off a response, normalised to a canonical
 * lowercase uuid. Returns null for anything else, so a malformed header never
 * reaches the UI.
 *
 * Requires the gateway to list the header in CORS expose_headers — without
 * that the browser hides it from JS and this always returns null.
 */
export function extractCorrelationId(resp: Response): string | null {
	const raw = resp.headers.get(CORRELATION_ID_HEADER);
	if (!raw || !UUID_RE.test(raw)) return null;
	return raw.toLowerCase();
}

/**
 * Appends a correlation-id reference to error text that's already shown
 * verbatim today (Message.error in MessageBubble, ActionResult.message in
 * ActionCard's failure Alert — neither is localized). No-op without an id.
 *
 * Never call this for a user-initiated Stop. MessageBubble matches the
 * 'stopped' sentinel exactly (message.error === 'stopped'), so any suffix
 * turns a deliberate cancel into an error bubble handing the user a support
 * reference for something they did on purpose. A mid-stream abort does not
 * arrive as a run failure — it arrives as a RUN_ERROR event, so the guard for
 * it lives in isAbortEvent below, not only in the two literal
 * finish({error: 'stopped'}) sites.
 */
function withCorrelationRef(text: string, correlationId: string | null): string {
	return correlationId ? `${text} (ref: ${correlationId})` : text;
}

/**
 * The shape POSTed to the gateway's /actions/{action_id}/resolve endpoint.
 * Mirrors gateway/actions.py:ResolveRequest in forecast-ai-agents — KEEP IN
 * SYNC. Wire contract documented in
 * DevEnvironment/forecast-ai-agents/docs/superpowers/specs/2026-06-23-tnsr-247-hitl-rework-design.md.
 *
 * `overrides` mirrors the planned gateway override field (cross-repo contract;
 * backend acceptance is a separate ticket). It is forwarded today and ignored
 * by the current backend; honoured once the backend round-trip ticket lands.
 */
export type ResolveRequest = {
	actionId: string;
	approved: boolean;
	entity_type: EntityType;
	entity_subtype: EntitySubtype;
	/** Entity IDs the user deselected; FE computes as full affected minus selected. */
	exclude_ids: number[];
	thread_id: string;
	/** Per-entity field edits keyed by entity ID. Absent when no edits were made. */
	overrides?: Record<string, Record<string, unknown>>;
	/** The same edits as the card showed them, for the model's next turn. Absent when none. */
	edited_values?: EditedValue[];
	/** A cancel re-sent after one failed in transport. A 409 to it means the first one landed. */
	retriedCancel?: boolean;
};

// HttpAgent does not expose a credentials option, so we subclass to add it.
// Also captures the gateway's correlation id off the response, before
// HttpAgent's own run() parses the SSE body.
//
// HttpAgent's AgentSubscriber callbacks carry no headers/response object, so
// there is no supported way to read the response through the public subscriber
// API. Instead the global `fetch` is wrapped around the single super.run(input)
// call below. That sees every response the agent receives regardless of status,
// including an immediate HTTP failure (backend 500, auth failure, rate limit)
// where no SSE body is ever streamed and no header-bearing stream event exists
// to read.
//
// Wrapping a global is only safe because the window is exactly one synchronous
// call: HttpAgent.run subscribes to the underlying request eagerly, so this
// run's fetch fires inside super.run()'s own call stack before it returns.
// There is no `await` between the wrap and the restore, so no other caller's
// fetch can be caught by the wrap and two hook instances cannot interleave.
// The paired tests assert both halves of that — window.fetch is the same
// object afterwards, and the id still arrives — so a library version that
// subscribed lazily instead would fail them rather than silently drop the id.
class CredentialedHttpAgent extends HttpAgent {
	private correlationIdRef: MutableRefObject<string | null>;

	constructor(config: HttpAgentConfig & {correlationIdRef: MutableRefObject<string | null>}) {
		super(config);
		this.correlationIdRef = config.correlationIdRef;
	}

	protected override requestInit(input: RunAgentInput): RequestInit {
		return {...super.requestInit(input), credentials: 'include'};
	}

	override run(input: RunAgentInput): ReturnType<HttpAgent['run']> {
		const ref = this.correlationIdRef;

		// Capture the exact reference, not `.bind(window)`: bind returns a NEW
		// function object, so the restore below would install a wrapper instead
		// of what was there before — stacking one bind frame on the app-wide
		// global per chat turn and dropping the own properties other fetch
		// instrumentation marks it with. `.call(window, ...)` supplies the
		// receiver the browser's WebIDL brand check demands without allocating
		// anything.
		const originalFetch = window.fetch;
		window.fetch = ((...args: Parameters<typeof fetch>) =>
			originalFetch.call(window, ...args).then(resp => {
				ref.current = extractCorrelationId(resp);
				return resp;
			})) as typeof fetch;

		try {
			return super.run(input);
		} finally {
			window.fetch = originalFetch;
		}
	}

	// AbstractAgent.clone() rebuilds the instance with Object.create() and copies
	// a fixed field list, so this constructor never runs for a clone and the ref
	// would be undefined — the wrapped fetch above would then throw on the
	// clone's first run. Share the same ref rather than a copy: a clone reports
	// into the same hook state.
	override clone(): CredentialedHttpAgent {
		const cloned = super.clone() as CredentialedHttpAgent;
		cloned.correlationIdRef = this.correlationIdRef;
		return cloned;
	}
}

// Process is provided by webpack DefinePlugin at build time
declare const process: {
	env: {
		REACT_APP_AI_DEPLOY_VERSION: string;
	};
};

const GATEWAY = AI_GATEWAY_URL;
const FORECAST_CHAT_AGENT_ID = 'forecast_chat';
const USER_APPROVAL_TOOL = 'user_approval_tool';
// How long after a card outcome the chat waits before asking the agent to carry on. Each further
// outcome inside the window restarts it, so a burst of outcomes produces one follow-up turn
// rather than one per card. Long enough to cover a quick run of clicks, short enough that the
// pause before the agent continues does not read as the chat having stalled.
const CONTINUATION_DEBOUNCE_MS = 800;
// A settled card outcome is sent on to the agent, because each one either changed something or
// tells the agent to stage, hold, re-stage or drop the steps that follow. The gateway reports a
// cancel as `failed`, so a cancel is covered too, but only when the gateway recorded it: a cancel
// answered locally as `expired` was never recorded, and telling the agent about it would make it
// offer the step the user just rejected again. A failed approval is sent on only when its guard
// is documented as non-retryable on `ActionResult.guard` (the plan drifted, was for another tool,
// or expired), so the agent has to stage a fresh card. Any other failed approval
// can succeed on Retry, and telling the agent the step failed lets it stage the same write again as
// a second card, so the write could be applied once from each. A transport failure never continues:
// the gateway recorded the plan as still open, or recorded nothing at all.
const CONTINUE_AFTER: ReadonlySet<ActionResult['status']> = new Set(['done', 'partial', 'unresolved', 'expired']);
const NON_RETRYABLE_GUARDS: ReadonlySet<ActionResult['guard']> = new Set(['expired', 'wrong_tool', 'drifted']);

function shouldContinueAfter(result: ActionResult, approved: boolean): boolean {
	if (isTransportFailure(result)) return false;
	if (!approved) return result.status === 'failed' || result.status === 'unresolved';
	if (CONTINUE_AFTER.has(result.status)) return true;
	if (result.status !== 'failed') return false;
	return NON_RETRYABLE_GUARDS.has(result.guard);
}

const SESSION_MESSAGES_KEY = 'ai-chat-messages';
const SESSION_THREAD_KEY = 'ai-chat-thread-id';
const SESSION_DEPLOY_KEY = 'ai-chat-deploy-version';
// The viewer/company the persisted conversation belongs to. sessionStorage is per-tab, so it
// outlives a sign-out and a sign-in as somebody else in that same tab; without this the next
// person would be shown the previous person's transcript.
const SESSION_IDENTITY_KEY = 'ai-chat-identity';

// Clear stale sessionStorage state when a new deployment is detected.
// REACT_APP_AI_DEPLOY_VERSION is the CI commit hash (empty in local dev).
// An empty version means we can't detect deployments — skip the check.
const DEPLOY_VERSION = process.env.REACT_APP_AI_DEPLOY_VERSION || '';

if (DEPLOY_VERSION) {
	try {
		const stored = sessionStorage.getItem(SESSION_DEPLOY_KEY);
		if (stored !== DEPLOY_VERSION) {
			sessionStorage.removeItem(SESSION_MESSAGES_KEY);
			sessionStorage.removeItem(SESSION_THREAD_KEY);
			sessionStorage.removeItem(SESSION_IDENTITY_KEY);
			sessionStorage.setItem(SESSION_DEPLOY_KEY, DEPLOY_VERSION);
		}
	} catch (_) {
		// no-op — sessionStorage may be unavailable
	}
}

// ── Domain types ──────────────────────────────────────────────────────────────

export type StatusStep = {
	id: string;
	stepType: 'thinking' | 'collating' | 'tool';
	done: boolean;
	toolName?: string;
	input?: Record<string, unknown>;
	output?: string;
	outputTruncated?: boolean;
};

export type Message = {
	id: string;
	role: 'user' | 'assistant';
	content: string;
	pending?: boolean;
	statusSteps?: StatusStep[];
	createdAt?: string;
	error?: string;
	/** Every write card this reply staged, in the order they arrived. */
	actionPlans?: ActionPlan[];
	/** Per-card outcome, keyed by `action_id`. A card with no entry is still open. */
	actionStates?: Record<string, ActionCardState>;
	/** A user turn with no text to show: the follow-up the chat sends on the user's behalf after a
	 * card settles, or an empty send. Never rendered, and never what regenerate re-sends. Persisted with the rest of
	 * the message, so a reload does not turn it into an empty bubble. */
	hidden?: boolean;
	// A create_report_intent proposal rendered as a HITL confirm card (not the write-action
	// ActionPlan path). The builder opens only after the user confirms and clicks View.
	reportIntent?: {
		payload: Record<string, unknown>;
		status: 'pending' | 'confirmed' | 'cancelled';
		/** Set once the builder saves this intent as a real report. From then on the card's View opens
		 * the live report by id instead of rebuilding a draft from `payload`, which goes stale the
		 * moment the report is edited. The card's own rendered content still comes from `payload` — the
		 * card is a message and must not rewrite itself. Persisted with the rest of the message. */
		savedReportId?: string;
	};
	/** The model hit its per-call token cap, so `content` stops mid-sentence.
	 *
	 * Bedrock reports a capped generation as an ordinary completion, so nothing
	 * else about the message says it is incomplete — without this the user is
	 * left to infer it from a reply that simply stops. Persisted with the rest
	 * of the message so the notice survives a reload, exactly as the truncated
	 * text it explains does. */
	truncated?: boolean;
};

type UseAIChatStreamReturn = {
	messages: Message[];
	setMessages: Dispatch<SetStateAction<Message[]>>;
	isStreaming: boolean;
	isHitlPending: boolean;
	sendMessage: (userText: string, allMessages: Message[], opts?: {hidden?: boolean}) => Promise<void>;
	abort: () => void;
	clearMessages: () => void;
	resetThread: () => void;
	respondToAction: (
		actionId: string,
		decision: {
			approved: boolean;
			selectedIds: string[];
			/** True when this cancel re-sends one that failed in transport, so a 409 means our own first cancel consumed the plan. */
			retriedCancel?: boolean;
			overrides?: Record<string, Record<string, unknown>>;
			editedValues?: EditedValue[];
		}
	) => Promise<ActionResult>;
	/** Applies a report-intent card's candidate pick through the gateway. Never throws. */
	applyReportPick: (payload: Record<string, unknown>, term: string, candidate: string) => Promise<ApplyReportPickResult>;
};

// ── Local AG-UI subscriber types ──────────────────────────────────────────────
// Mirrors the relevant subset of AgentSubscriber from @ag-ui/client.
// Defined locally so TypeScript can infer handler parameter types without
// requiring the IDE to resolve @ag-ui/client from node_modules/.ignored/.

type StepEvt = {stepName: string};
type ToolCallStartEvt = {toolCallId: string; toolCallName: string};
type ToolCallEndEvt = {toolCallId: string};
type ToolCallResultEvt = {toolCallId: string; content: string};
type TextMessageContentEvt = {delta: string};
type RunErrorEvt = {message: string; code?: string; rawEvent?: unknown};

type AIChatSubscriber = {
	onStepStartedEvent?: (params: {event: StepEvt}) => void;
	onStepFinishedEvent?: (params: {event: StepEvt}) => void;
	onToolCallStartEvent?: (params: {event: ToolCallStartEvt}) => void;
	onToolCallEndEvent?: (params: {event: ToolCallEndEvt; toolCallArgs: Record<string, unknown>}) => void;
	onToolCallResultEvent?: (params: {event: ToolCallResultEvt}) => void;
	onTextMessageStartEvent?: () => void;
	onTextMessageContentEvent?: (params: {event: TextMessageContentEvt}) => void;
	onRunFinishedEvent?: () => void;
	onRunErrorEvent?: (params: {event: RunErrorEvt}) => void;
	onRunFailed?: (params: {error: Error}) => void;
};

/**
 * True when a RUN_ERROR event is the library's report of a user-initiated
 * abort rather than a real failure.
 *
 * Clicking Stop mid-stream rejects the body reader with an AbortError, which
 * @ag-ui/client turns into a RUN_ERROR event carrying code 'abort' and then
 * completes the stream normally. So the run neither fails nor throws: onRunFailed
 * and the awaited runAgent's catch are both skipped, and this is the only place
 * left that can recognise the cancel and emit the 'stopped' sentinel instead of
 * an error message.
 */
function isAbortEvent(event: RunErrorEvt): boolean {
	if (event.code === 'abort') return true;
	const raw = event.rawEvent as {name?: unknown} | null | undefined;
	return typeof raw === 'object' && raw !== null && raw.name === 'AbortError';
}

/**
 * The refusals the gateway makes before it ever calls the model, as a
 * status/code pair each: the account's AI allowance is spent, the gen AI kill
 * switch is set for the account or the whole platform, and the viewer's
 * profile does not grant AI_ACCESS. The last one also covers a permission
 * removed while the chat is open, before the entry points have been hidden.
 */
const GATEWAY_REFUSALS: ReadonlyArray<{status: number; code: string}> = [
	{status: 402, code: 'BUDGET_EXHAUSTED'},
	{status: 403, code: 'GEN_AI_DISABLED'},
	{status: 403, code: 'AI_ACCESS_DENIED'},
];

/**
 * The sentence the gateway wrote for a refusal it made before calling the
 * model, or null when the failure is anything else.
 *
 * No refusal is a fault and none must read like one, so the caller
 * renders the returned sentence as an ordinary reply rather than an error: the
 * user sees what happened instead of a status code and a support reference.
 *
 * A 503 is deliberately absent. It means the gateway could not confirm the
 * state, which is a retryable outage and belongs in the error path.
 *
 * `@ag-ui/client` throws `HTTP <status>: <body>` for any non-ok response but
 * also hangs the parts off the error, so the refusal can be recognised without
 * parsing that string.
 *
 * A spent allowance is also announced, so the credit notices refresh at once.
 */
function gatewayRefusalMessage(error: unknown): string | null {
	const carrier = error as {status?: unknown; payload?: unknown} | null | undefined;
	if (!carrier) return null;

	const payload = carrier.payload as {error?: {code?: unknown; message?: unknown}} | undefined;
	if (!payload || typeof payload !== 'object') return null;

	const code = payload.error?.code;
	if (!GATEWAY_REFUSALS.some(refusal => refusal.status === carrier.status && refusal.code === code)) return null;
	if (code === 'BUDGET_EXHAUSTED') notifyAiCreditsExhausted();

	const message = payload.error?.message;
	if (typeof message !== 'string') return null;

	const trimmed = message.trim();
	return trimmed.length > 0 ? trimmed : null;
}

// ── Persistence helpers ───────────────────────────────────────────────────────

function readPersistedMessages(): Message[] {
	try {
		const raw = sessionStorage.getItem(SESSION_MESSAGES_KEY);
		return raw ? (JSON.parse(raw) as (Message & LegacyActionFields)[]).map(normalizePersistedMessage) : [];
	} catch (_) {
		return [];
	}
}

function readOrCreateThreadId(): string {
	try {
		const stored = sessionStorage.getItem(SESSION_THREAD_KEY);
		if (stored) return stored;
		const id = uuid();
		sessionStorage.setItem(SESSION_THREAD_KEY, id);
		return id;
	} catch (_) {
		return uuid();
	}
}

function readPersistedIdentity(): string | null {
	try {
		return sessionStorage.getItem(SESSION_IDENTITY_KEY);
	} catch (_) {
		return null;
	}
}

/** Writes the conversation out under the identity that owns it. Pending messages are not stored. */
function writePersistedConversation(messages: Message[], identity: string): void {
	try {
		const toStore = messages.filter((m: Message) => !m.pending);
		sessionStorage.setItem(SESSION_MESSAGES_KEY, JSON.stringify(toStore));
		sessionStorage.setItem(SESSION_IDENTITY_KEY, identity);
	} catch (_) {
		// no-op — sessionStorage may be unavailable
	}
}

/** Forgets the persisted conversation so the next read starts a fresh one under `identity`. */
function discardPersistedConversation(identity: string): void {
	try {
		sessionStorage.removeItem(SESSION_MESSAGES_KEY);
		sessionStorage.removeItem(SESSION_THREAD_KEY);
		sessionStorage.setItem(SESSION_IDENTITY_KEY, identity);
	} catch (_) {
		// no-op — sessionStorage may be unavailable
	}
}

// ── Tool label lookup ─────────────────────────────────────────────────────────

// Tools emitted by the gateway that are internal signals — never shown in UI.
enum SentinelTool {
	CreateProjectIntent = 'create_project_intent',
	CreateReportIntent = 'create_report_intent',
	// Not a tool the model calls: the gateway synthesises this call when the
	// answer above it was cut off by the per-call token cap. It carries no
	// TOOL_CALL_RESULT, so it is read in onToolCallEndEvent rather than
	// alongside the two intents below, which do.
	ResponseTruncated = 'response_truncated',
}
const SENTINEL_TOOLS: Set<string> = new Set(Object.values(SentinelTool));

// ── Default resolveAction transport ──────────────────────────────────────────
//
// POSTs `req` to the gateway's /actions/{actionId}/resolve endpoint with
// the same auth headers as the SSE stream. Used when the caller of
// useAIChatStream doesn't supply a custom `resolveAction` prop. Lives at
// module scope (not in a closure) so tests can import + assert on it.
//
// Wire contract documented in:
//   forecast-ai-agents/docs/superpowers/specs/2026-06-23-tnsr-247-hitl-rework-design.md
//   forecast-ai-agents/gateway/actions.py:resolve_action

export type ResolveTransportAuth = {
	/** Optional. In-app Nova authenticates with the `sessionKey` cookie (`credentials: 'include'`),
	 * which the gateway and the MCP layer both accept — the API key path is only for callers that
	 * hold one, and minting one requires admin rights the caller may not have. */
	apiKey?: string;
	viewerPersonId?: number;
	companyId?: string;
	companyName?: string;
};

/** The JSON + auth headers every non-stream gateway POST sends, matching the SSE stream's own. */
function transportHeaders(auth: ResolveTransportAuth): Record<string, string> {
	const headers: Record<string, string> = {'Content-Type': 'application/json'};
	if (auth.apiKey) headers['X-Forecast-Api-Key'] = auth.apiKey;
	if (auth.viewerPersonId != null) headers['X-Viewer-Person-Id'] = String(auth.viewerPersonId);
	if (auth.companyId) headers['X-Forecast-Company-Id'] = auth.companyId;
	if (auth.companyName) headers['X-Forecast-Company-Name'] = auth.companyName;
	const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
	if (tz) headers['X-User-Timezone'] = tz;
	return headers;
}

// Gateway statuses that mean the plan is gone: not found, already consumed, expired.
const CANCEL_GONE_STATUSES: ReadonlySet<number> = new Set([404, 410]);
const CANCEL_CONSUMED_STATUS = 409;

export async function defaultResolveActionTransport(req: ResolveRequest, auth: ResolveTransportAuth): Promise<ActionResult> {
	const headers = transportHeaders(auth);

	const url = `${GATEWAY}/actions/${encodeURIComponent(req.actionId)}/resolve`;

	let resp: Response;
	try {
		resp = await fetch(url, {
			method: 'POST',
			credentials: 'include',
			headers,
			body: JSON.stringify({
				approved: req.approved,
				entity_type: req.entity_type,
				entity_subtype: req.entity_subtype,
				exclude_ids: req.exclude_ids,
				thread_id: req.thread_id,
				overrides: req.overrides,
				edited_values: req.edited_values,
			}),
		});
	} catch (e) {
		return {status: 'failed', guard: 'transport', message: e instanceof Error ? e.message : 'Network error'};
	}

	// A cancel the gateway no longer knows (expired or lost to a restart) is already resolved, so
	// the card is dismissed instead of retrying a cancel that can never succeed. A plan the gateway
	// already consumed may have been applied, so that is reported as unresolved, never as cancelled.
	if (!req.approved) {
		if (CANCEL_GONE_STATUSES.has(resp.status)) {
			return {status: 'expired', guard: 'expired', message: 'This plan is no longer open.'};
		}
		if (resp.status === CANCEL_CONSUMED_STATUS && req.retriedCancel) {
			return {status: 'expired', guard: 'expired', message: 'This plan is no longer open.'};
		}
		if (resp.status === CANCEL_CONSUMED_STATUS) {
			return {status: 'unresolved', message: 'This plan was already used.'};
		}
	}

	// Treat every other non-2xx as a transport failure so the FE can show Retry; the
	// auth layer above will refresh credentials on the next attempt.
	if (!resp.ok) {
		let detail = '';
		try {
			const j = await resp.json();
			detail = typeof j?.detail === 'string' ? j.detail : JSON.stringify(j);
		} catch {
			detail = `HTTP ${resp.status}`;
		}
		return {status: 'failed', guard: 'transport', message: withCorrelationRef(detail, extractCorrelationId(resp))};
	}

	try {
		const parsed = (await resp.json()) as ActionResult;
		return parsed;
	} catch (e) {
		return {
			status: 'failed',
			guard: 'transport',
			message: withCorrelationRef(e instanceof Error ? e.message : 'Malformed response', extractCorrelationId(resp)),
		};
	}
}

// ── Default apply-pick transport ─────────────────────────────────────────────
//
// POSTs a report-intent card's candidate pick to the gateway's
// /report-intents/apply-pick endpoint, which patches the draft with no model
// call and answers with the new card payload. Mirrors
// gateway/report_intents.py:ApplyPickRequest in forecast-ai-agents — KEEP IN SYNC.

export type ApplyReportPickRequest = {
	thread_id: string;
	/** The card's current payload, echoed back whole: the gateway holds no copy of the draft. */
	payload: Record<string, unknown>;
	/** The unmatched value, exactly as the card's unresolved flag names it. */
	term: string;
	/** The candidate the user picked, one of that flag's own candidates. */
	candidate: string;
};

export type ApplyReportPickResult =
	| {status: 'applied'; payload: Record<string, unknown>}
	// The gateway will never apply this pick, so it goes through the chat instead and the agent
	// redrafts from it: a gateway that predates the endpoint (404), or any refusal that depends only
	// on the payload sent (409, 413, 422 — a card older than the endpoint, a pick the draft cannot
	// take, a draft too large to hand off, a body the endpoint rejects). Retrying would get the same
	// answer, so the card must not offer that. `code` is the gateway's own error code, when it sent one.
	| {status: 'unsupported'; code?: string}
	// A failure a retry may get past: the network, the client timeout, auth (401/403) or the gateway
	// itself (5xx). `refusalMessage` is the gateway's own sentence for a refusal it makes before any
	// work (the gen AI kill switch, a spent allowance), for the card to show instead of its own copy.
	| {status: 'failed'; message: string; code?: string; refusalMessage?: string};

/**
 * How long the card waits for a pick before giving up and offering a retry. The gateway's own bounds
 * run to its ~300s upstream read timeout, and while a pick is pending every card refuses picks and
 * this card's candidates, Confirm and Cancel are all disabled, so the client stops waiting well
 * before that.
 */
export const APPLY_PICK_TIMEOUT_MS = 25_000;

/** The statuses whose refusal depends only on the payload sent, so the same pick can never succeed. */
const APPLY_PICK_PAYLOAD_REFUSAL_STATUSES: ReadonlySet<number> = new Set([409, 413, 422]);

/** Every pick the gateway did not apply is logged with its code and correlation ref, so it can be
 * traced without the user having to report it. */
function logPickNotApplied(
	result: ApplyReportPickResult,
	detail: {httpStatus?: number; correlationId?: string | null; message?: string}
): ApplyReportPickResult {
	if (result.status !== 'applied') {
		console.warn('[applyReportPick] pick not applied', {
			outcome: result.status,
			code: result.code,
			httpStatus: detail.httpStatus,
			ref: detail.correlationId ?? undefined,
			message: detail.message,
		});
	}
	return result;
}

const APPLY_PICK_TIMED_OUT = 'Timed out';

async function requestApplyPick(
	req: ApplyReportPickRequest,
	auth: ResolveTransportAuth,
	signal: AbortSignal
): Promise<ApplyReportPickResult> {
	let resp: Response;
	try {
		resp = await fetch(`${GATEWAY}/report-intents/apply-pick`, {
			method: 'POST',
			credentials: 'include',
			headers: transportHeaders(auth),
			body: JSON.stringify(req),
			signal,
		});
	} catch (e) {
		const message = signal.aborted ? APPLY_PICK_TIMED_OUT : e instanceof Error ? e.message : 'Network error';
		return logPickNotApplied({status: 'failed', message}, {message});
	}
	const correlationId = extractCorrelationId(resp);

	// The handler never answers 404 itself, so a 404 is a gateway without the route. CORS wraps
	// every response, the unrouted one included, so the status is readable here.
	if (resp.status === 404) return logPickNotApplied({status: 'unsupported'}, {httpStatus: 404, correlationId});

	// The gateway's error envelope is {"error": {code, message}}, except the missing-auth 401, whose
	// "error" is the message itself; FastAPI's own body validation answers {"detail": [...]}.
	if (!resp.ok) {
		let body: unknown;
		try {
			body = await resp.json();
		} catch {
			// An unreadable body falls through to the status line below.
		}
		if (signal.aborted) {
			return logPickNotApplied(
				{status: 'failed', message: APPLY_PICK_TIMED_OUT},
				{httpStatus: resp.status, correlationId}
			);
		}
		let code: string | undefined;
		let message = '';
		const j = body as {error?: unknown; detail?: unknown} | null | undefined;
		const error = j?.error;
		if (typeof error === 'string') {
			message = error;
		} else if (error && typeof error === 'object') {
			const e = error as {code?: unknown; message?: unknown};
			if (typeof e.code === 'string') code = e.code;
			if (typeof e.message === 'string') message = e.message;
		}
		if (!message && typeof j?.detail === 'string') message = j.detail;
		const detail = {httpStatus: resp.status, correlationId, message};

		if (APPLY_PICK_PAYLOAD_REFUSAL_STATUSES.has(resp.status)) {
			return logPickNotApplied(code ? {status: 'unsupported', code} : {status: 'unsupported'}, detail);
		}

		// 401/403/5xx: the auth layer above refreshes credentials on the next attempt, and an outage
		// may clear. The kill switch (403 GEN_AI_DISABLED) is the same refusal the chat renders, so its
		// sentence is carried through for the card to show.
		const failed: Extract<ApplyReportPickResult, {status: 'failed'}> = {
			status: 'failed',
			message: withCorrelationRef(message || `HTTP ${resp.status}`, correlationId),
		};
		if (code) failed.code = code;
		const refusalMessage = gatewayRefusalMessage({status: resp.status, payload: body});
		if (refusalMessage) failed.refusalMessage = refusalMessage;
		return logPickNotApplied(failed, detail);
	}

	let body: unknown;
	try {
		body = await resp.json();
	} catch {
		body = undefined;
	}
	if (signal.aborted) {
		return logPickNotApplied({status: 'failed', message: APPLY_PICK_TIMED_OUT}, {httpStatus: resp.status, correlationId});
	}
	const next = (body as {payload?: unknown} | null | undefined)?.payload;
	if (!next || typeof next !== 'object' || Array.isArray(next)) {
		return logPickNotApplied(
			{status: 'failed', message: withCorrelationRef('Malformed response', correlationId)},
			{httpStatus: resp.status, correlationId}
		);
	}
	return {status: 'applied', payload: next as Record<string, unknown>};
}

export async function defaultApplyReportPickTransport(
	req: ApplyReportPickRequest,
	auth: ResolveTransportAuth,
	timeoutMs: number = APPLY_PICK_TIMEOUT_MS
): Promise<ApplyReportPickResult> {
	// One signal bounds the whole exchange, body read included: a response whose body stalls holds
	// the card just as long as a request that never answers.
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), timeoutMs);
	try {
		return await requestApplyPick(req, auth, controller.signal);
	} finally {
		clearTimeout(timer);
	}
}

// ── Relay store helpers ───────────────────────────────────────────────────────

export type DeleteProjectResult = {
	/** Relay global ID (base64 node ID) of the deleted project. */
	relayNodeId: string;
	/** customProjectId slug (e.g. "P-42") read from the Relay store before deletion — used to compare against the URL. undefined when the record was not found in the store. */
	customProjectId: string | undefined;
};

/**
 * Removes a deleted project from the Relay viewer connections and returns its IDs.
 * Called after the MCP delete_project tool succeeds so the project list updates
 * immediately without a page reload. Mirrors the RANGE_DELETE configs in
 * delete_project_mutation.js (must be kept in sync if new connection keys are added).
 *
 * Returns undefined when content indicates no deletion (deleted !== true, missing
 * id, bad JSON, or non-string project_id). The relayNodeId must be the base64
 * Relay node ID — a plain integer companyProjectId won't match any Relay node.
 */
export function applyDeleteProjectToRelayStore(content: string, fallbackProjectId?: string): DeleteProjectResult | undefined {
	let parsed: Record<string, unknown>;
	try {
		parsed = JSON.parse(content) as Record<string, unknown>;
	} catch {
		return undefined;
	}
	const rawId = parsed.project_id ?? fallbackProjectId;
	const projectId = typeof rawId === 'string' ? rawId : undefined;
	if (parsed.deleted !== true || !projectId) return undefined;
	let customProjectId: string | undefined;
	commitLocalUpdate(RelayEnvironment.getInstance(), store => {
		const record = store.get(projectId);
		customProjectId = record?.getValue('customProjectId') as string | undefined;
		const viewer = store.getRoot().getLinkedRecord('viewer');
		if (!viewer) return;
		const conn = ConnectionHandler.getConnection(viewer, 'Viewer_projects');
		if (conn) ConnectionHandler.deleteNode(conn, projectId);
		const headerConn = ConnectionHandler.getConnection(viewer, 'Header_Viewer_projects');
		if (headerConn) ConnectionHandler.deleteNode(headerConn, projectId);
	});
	return {relayNodeId: projectId, customProjectId};
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAIChatStream({
	apiKey,
	viewerPersonId,
	companyId,
	companyName,
	onProjectIntent,
	onReportIntent,
	onProjectDeleted,
	resolveAction,
	applyReportPickTransport,
}: {
	/** Optional — see ResolveTransportAuth.apiKey. Absent means session-cookie auth. */
	apiKey?: string;
	viewerPersonId?: number;
	companyId?: string;
	companyName?: string;
	onProjectIntent?: (data: Record<string, unknown>) => void;
	onReportIntent?: (data: Record<string, unknown>) => void;
	/** Called after delete_project succeeds. projectId is the Relay node ID; customProjectId is the URL slug (e.g. "P-42") read from the store — compare against the URL to navigate away only from the deleted project's page. */
	onProjectDeleted?: (projectId: string, customProjectId?: string) => void;
	resolveAction?: (req: ResolveRequest) => Promise<ActionResult>;
	/** Replaces the default apply-pick fetch; tests stub it. */
	applyReportPickTransport?: (req: ApplyReportPickRequest) => Promise<ApplyReportPickResult>;
}): UseAIChatStreamReturn {
	// Starts empty rather than seeded from storage: viewerPersonId arrives with the Relay viewer
	// query, so on the first render there is no way to tell whose transcript is in storage.
	// Seeding it and clearing later would put the previous person's conversation on screen first.
	const [messages, setMessages] = useState<Message[]>([]);
	const [isStreaming, setIsStreaming] = useState(false);
	const [isHitlPending, setIsHitlPending] = useState(false);
	const agentRef = useRef<HttpAgent | null>(null);
	// Analytics read the thread id live from the agent, so every rotation (clear, regenerate, identity
	// change, deploy) is picked up without a re-register. Unregistered on unmount, so an unmounted
	// panel's agent is neither reported nor kept alive.
	useEffect(() => setNovaThreadIdSource(() => agentRef.current?.threadId ?? null), []);
	const stepsRef = useRef<StatusStep[]>([]);
	const prevApiKeyRef = useRef<string | null>(null);
	// The identity baked into the current agent's headers, so the init effect below can tell a
	// re-render apart from an actual change of viewer or company.
	const prevIdentityRef = useRef<string | null>(null);
	// Correlation id off the most recent /invocations response, captured by
	// CredentialedHttpAgent.run(). Reset at the start of each sendMessage so a
	// turn whose fetch never resolves (e.g. request never reaches the network)
	// can't surface a stale id left over from an earlier, unrelated turn.
	const correlationIdRef = useRef<string | null>(null);
	const viewerPersonIdRef = useRef<number | undefined>(viewerPersonId);
	viewerPersonIdRef.current = viewerPersonId;
	const companyIdRef = useRef<string | undefined>(companyId);
	companyIdRef.current = companyId;
	const companyNameRef = useRef<string | undefined>(companyName);
	companyNameRef.current = companyName;
	const currentIdentity = () => `${viewerPersonId ?? ''}:${companyId ?? ''}:${companyName ?? ''}`;
	const identityChanged = () => prevIdentityRef.current !== currentIdentity();
	// False until the rehydrate effect below has decided whether the persisted conversation
	// belongs to this viewer. Until then nothing may be written back over it, and the stored
	// thread id may be the previous viewer's, so the agent must not pick it up either.
	const hydratedRef = useRef(false);
	// Set when that decision has been made, either way, so the agent re-reads the thread id out
	// of storage instead of carrying the one it was built with: a kept conversation resumes on
	// its own thread, a discarded one gets a fresh thread the gateway has no history for.
	const rereadThreadRef = useRef(false);
	// The array `messages` starts out as. Identity-compares against it to tell the placeholder
	// apart from a real (possibly empty) transcript, so the placeholder is never persisted.
	const initialMessagesRef = useRef(messages);
	// Set when the persist effect declined to write because the viewer was unresolved. The effect
	// is keyed on the message array alone, and a viewer resolving back to the same identity changes
	// no array, so without this the skipped write is dropped rather than deferred and a reload
	// loses every message that arrived during the window.
	const pendingPersistRef = useRef(false);
	const onProjectIntentRef = useRef<((data: Record<string, unknown>) => void) | undefined>(onProjectIntent);
	onProjectIntentRef.current = onProjectIntent;
	const onReportIntentRef = useRef<((data: Record<string, unknown>) => void) | undefined>(onReportIntent);
	onReportIntentRef.current = onReportIntent;
	const onProjectDeletedRef = useRef<((projectId: string, customProjectId?: string) => void) | undefined>(onProjectDeleted);
	onProjectDeletedRef.current = onProjectDeleted;
	const resolveActionRef = useRef<typeof resolveAction>(resolveAction);
	resolveActionRef.current = resolveAction;
	const applyReportPickTransportRef = useRef<typeof applyReportPickTransport>(applyReportPickTransport);
	applyReportPickTransportRef.current = applyReportPickTransport;
	// Mirror of the latest messages so respondToAction (useCallback []) can read
	// the current ActionPlan without re-creating the callback or abusing setMessages.
	const messagesRef = useRef<Message[]>(messages);
	messagesRef.current = messages;
	// The follow-up turn after a card outcome. The timer is the debounce window; `continuationOwedRef`
	// holds a follow-up whose window closed while a reply was still streaming, to be sent when it ends.
	const continuationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const continuationOwedRef = useRef(false);
	const isStreamingRef = useRef(isStreaming);
	isStreamingRef.current = isStreaming;
	const sendMessageRef = useRef<UseAIChatStreamReturn['sendMessage'] | null>(null);

	// Headers a CredentialedHttpAgent bakes in at construction: the credential when the caller
	// has one, the viewer/company identity the gateway attributes the run to, timezone, agent id.
	// Shared by the init effect and rebuildAgent so the two cannot drift.
	function buildAgentHeaders(): Record<string, string> {
		const headers: Record<string, string> = {};
		const key = prevApiKeyRef.current;
		if (key) headers['X-Forecast-Api-Key'] = key;
		if (viewerPersonIdRef.current != null) headers['X-Viewer-Person-Id'] = String(viewerPersonIdRef.current);
		// Tenant headers for telemetry. Omit entirely when unavailable —
		// the gateway treats absent and empty headers differently.
		if (companyIdRef.current) headers['X-Forecast-Company-Id'] = companyIdRef.current;
		if (companyNameRef.current) headers['X-Forecast-Company-Name'] = companyNameRef.current;
		const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		if (userTimeZone) headers['X-User-Timezone'] = userTimeZone;
		headers['x-agent-id'] = FORECAST_CHAT_AGENT_ID;
		return headers;
	}

	// ── Follow-up after a card outcome ─────────────────────────────────────────────
	// A card outcome is settled out of band and starts no turn, so without this the agent waits for the
	// user to type before it can act on it. Refs only: respondToAction, which schedules
	// it, is a stable callback.
	function cancelContinuation(): void {
		if (continuationTimerRef.current) clearTimeout(continuationTimerRef.current);
		continuationTimerRef.current = null;
		continuationOwedRef.current = false;
	}
	function sendContinuation(): void {
		void sendMessageRef.current?.('', messagesRef.current, {hidden: true});
	}
	function scheduleContinuation(): void {
		if (continuationTimerRef.current) clearTimeout(continuationTimerRef.current);
		continuationTimerRef.current = setTimeout(() => {
			continuationTimerRef.current = null;
			if (isStreamingRef.current) continuationOwedRef.current = true;
			else sendContinuation();
		}, CONTINUATION_DEBOUNCE_MS);
	}
	useEffect(() => {
		if (isStreaming || !continuationOwedRef.current) return;
		continuationOwedRef.current = false;
		sendContinuation();
	}, [isStreaming]);
	useEffect(() => cancelContinuation, []);

	// Decide who the persisted conversation belongs to, before the agent-init effect below reads
	// the thread id out of storage. Declaration order is the ordering guarantee: React runs layout
	// effects in the order they are declared, within the same commit.
	//
	// While viewerPersonId is undefined the viewer query has not resolved yet, and the stored
	// conversation is left exactly as it is — neither shown nor destroyed. Deciding on a half-known
	// identity would throw away a signed-in person's own conversation on every page load.
	useLayoutEffect(() => {
		if (viewerPersonId == null) return;
		const identity = currentIdentity();
		// The analytics session sits in the same per-tab storage, so it is held to the same owner.
		claimNovaSession(identity);
		// Anything already in state before the first decision was typed in this mount, by whoever
		// is signing in now: `messages` no longer seeds from storage, so it cannot be somebody
		// else's. It survives the decision either way. After that first decision a mismatch means
		// the identity changed under a live conversation, and that one does have to go.
		const decided = hydratedRef.current;
		const typedBeforeDecision = decided ? [] : messagesRef.current;
		if (readPersistedIdentity() === identity) {
			// This viewer's own conversation - a reload, or a credential change. `hydratedRef`
			// keeps a repeat run from re-reading what is already in state.
			if (!decided) setMessages([...readPersistedMessages(), ...typedBeforeDecision]);
			// The same viewer is back after an unresolved spell. Nothing changed the message array
			// while they were away, so the persist effect will not run on its own - flush the write
			// it declined to make, or the messages from that window exist only on screen.
			else if (pendingPersistRef.current) writePersistedConversation(messagesRef.current, identity);
		} else {
			// A different person or company, or a conversation stored before identities were
			// recorded. Either way it is not this viewer's to read.
			discardPersistedConversation(identity);
			// A copy, not the array itself: the new reference is what wakes the persist effect, so
			// whatever was typed before the decision is written out under the identity that owns it.
			setMessages([...typedBeforeDecision]);
		}
		// Either branch has now settled storage for this identity: the match branch flushed or
		// restored, the mismatch branch discarded and hands setMessages a fresh array the persist
		// effect will pick up. Nothing is owed.
		pendingPersistRef.current = false;
		rereadThreadRef.current = true;
		hydratedRef.current = true;
	}, [viewerPersonId, companyId, companyName]);

	// (Re-)init the HttpAgent whenever anything baked into its headers changes: the optional
	// api key, and the viewer/company identity. HttpAgent takes its headers once at
	// construction, so a panel that mounts before the viewer query resolves would otherwise
	// send every turn of its life with no person or tenant header — the gateway would lose the
	// subject of the trace and fall back to re-deriving it from the session.
	// useLayoutEffect (synchronous, pre-paint) preserves the timing guarantee
	// of the previous render-phase init without being a side-effect-in-render.
	useLayoutEffect(() => {
		if (agentRef.current && (!apiKey || apiKey === prevApiKeyRef.current) && !identityChanged()) return;
		prevApiKeyRef.current = apiKey ?? null;
		prevIdentityRef.current = currentIdentity();
		const outgoing = agentRef.current;
		if (outgoing) {
			// sendMessage captures the agent instance it streams against, so replacing the ref
			// under an in-flight run would leave that run going with nothing able to stop it:
			// a later Stop or unmount aborts the fresh, never-started instance instead. The run
			// also belongs to the previous viewer or company, which is why we are here at all.
			outgoing.abortRun();
			setIsStreaming(false);
			setIsHitlPending(false);
			cancelContinuation();
		}
		// Keep the thread when only the credentials changed - same conversation, same viewer. Re-read
		// it from storage once the rehydrate effect above has ruled on the persisted conversation:
		// kept, the stored thread is this viewer's own; discarded, the thread id went with it and
		// this mints a fresh one the gateway has no history for.
		//
		// Before that ruling the stored thread id may belong to whoever used this tab last, and the
		// gateway would resume THEIR history on it - so a run started in that window gets a
		// throwaway thread that is deliberately not written to storage.
		const reread = rereadThreadRef.current;
		rereadThreadRef.current = false;
		agentRef.current = new CredentialedHttpAgent({
			url: `${GATEWAY}/invocations`,
			threadId: outgoing && !reread ? outgoing.threadId : hydratedRef.current ? readOrCreateThreadId() : uuid(),
			headers: buildAgentHeaders(),
			correlationIdRef,
		});
	}, [apiKey, viewerPersonId, companyId, companyName]);

	useEffect(() => {
		// Before the rehydrate effect has run, `messages` is the empty placeholder, not this
		// viewer's transcript - writing it out would destroy whatever is in storage. The same
		// applies to the placeholder itself on the commit the rehydrate decides in: that commit's
		// `messages` is still the one the hook was initialised with, and the restored transcript
		// only lands on the next one.
		if (!hydratedRef.current || messages === initialMessagesRef.current) return;
		// An unresolved viewer cannot own anything. Stamping the empty identity here would make
		// the viewer's own conversation look like somebody else's the moment it resolves again.
		// The write is owed, not abandoned: the rehydrate effect makes it once the viewer is known.
		if (viewerPersonId == null) {
			pendingPersistRef.current = true;
			return;
		}
		writePersistedConversation(messages, currentIdentity());
	}, [messages]);

	// ── Step state helpers (sync ref mutations, no re-render until syncSteps) ──
	function resetSteps(): void {
		stepsRef.current = [];
	}
	function pushStep(step: Omit<StatusStep, 'id' | 'done'>): string {
		const id = uuid();
		stepsRef.current = [...stepsRef.current, {id, done: false, ...step}];
		return id;
	}
	function updateStep(id: string, patch: Partial<StatusStep>): void {
		stepsRef.current = stepsRef.current.map((s: StatusStep) => (s.id === id ? {...s, ...patch} : s));
	}
	function doneAll(): void {
		stepsRef.current = stepsRef.current.map((s: StatusStep) => ({...s, done: true}));
	}
	function syncSteps(msgId: string): void {
		const snapshot = [...stepsRef.current];
		setMessages((prev: Message[]) => prev.map((m: Message) => (m.id === msgId ? {...m, statusSteps: snapshot} : m)));
	}

	// Rebuild the AG-UI agent bound to a fresh thread_id. A new thread starts the
	// LangGraph checkpointer from empty, so any pending/expired HITL interrupt on
	// the previous thread is abandoned rather than resumed.
	const rebuildAgent = useCallback((newThreadId: string): void => {
		try {
			sessionStorage.setItem(SESSION_THREAD_KEY, newThreadId);
		} catch (_) {
			// no-op — sessionStorage may be unavailable
		}
		agentRef.current = new CredentialedHttpAgent({
			url: `${GATEWAY}/invocations`,
			threadId: newThreadId,
			headers: buildAgentHeaders(),
			correlationIdRef,
		});
	}, []);

	// ── Reset thread (keep transcript) ─────────────────────────────────────────
	// Abort any in-flight stream, reset flags, and start a fresh thread. Does NOT
	// wipe the visible transcript. Used by regenerate so the resent query runs on
	// a clean thread and a stale (e.g. expired) pending action isn't carried
	// forward by the agent. rebuildAgent re-persists SESSION_THREAD_KEY.
	const resetThread = useCallback((): void => {
		cancelContinuation();
		if (agentRef.current) agentRef.current.abortRun();
		setIsStreaming(false);
		setIsHitlPending(false);
		rebuildAgent(uuid());
	}, [rebuildAgent]);

	// ── Clear ─────────────────────────────────────────────────────────────────
	const clearMessages = useCallback(() => {
		try {
			sessionStorage.removeItem(SESSION_MESSAGES_KEY);
		} catch (_) {
			// no-op — sessionStorage may be unavailable
		}
		setMessages([]);
		// resetThread aborts the stream, resets flags, and re-inits the agent on a
		// fresh thread so LLM context is also reset.
		resetThread();
	}, [resetThread]);

	// ── Abort ─────────────────────────────────────────────────────────────────
	const abort = useCallback(() => {
		// Stop means stop: a follow-up held behind this reply must not start the moment it ends.
		cancelContinuation();
		if (agentRef.current) agentRef.current.abortRun();
	}, []);

	// ── Respond to an approval action card ─────────────────────────────────────
	//
	// The card calls this with `{approved, selectedIds}`. We translate that
	// into the gateway's resolve contract:
	//   - look up the message's ActionPlan for entity_type / entity_subtype
	//     and the full affected list
	//   - compute `exclude_ids` = (full_affected_ids - selectedIds), coerced
	//     to integers (MCP expects list[int]). For Confirm with no
	//     deselection, exclude_ids is empty.
	//   - pre-flight `expires_at`: if an approval arrives after the plan has
	//     already expired on the client clock, short-circuit to
	//     ActionResult.status='expired' without round-tripping. A rejection
	//     goes to the gateway, so the server records the cancel, unless the
	//     card is already settled as expired, which is dismissed locally.
	//   - read thread_id from the HttpAgent so /actions/resolve can mutate
	//     the same LangGraph thread.
	const respondToAction = useCallback(
		async (
			actionId: string,
			decision: {
				approved: boolean;
				selectedIds: string[];
				/** True when this cancel re-sends one that failed in transport, so a 409 means our own first cancel consumed the plan. */
				retriedCancel?: boolean;
				overrides?: Record<string, Record<string, unknown>>;
				editedValues?: EditedValue[];
			}
		): Promise<ActionResult> => {
			let result: ActionResult;
			// Clear, regenerate and an identity change each replace the agent. A confirm that settles
			// after that belongs to a conversation that is gone, so it must not start a turn in the new one.
			const agentAtConfirm = agentRef.current;
			try {
				// Read the current ActionPlan from the messages ref. Lookup is
				// best-effort; if the message/plan is missing for any reason,
				// fail closed with a clear error so the FE knows.
				const plan = findActionPlan(messagesRef.current, actionId);
				if (!plan) {
					return {status: 'failed', guard: 'transport', message: 'Action plan not found in chat state'};
				}

				// A card already settled as expired has no open plan at the gateway, so cancelling it only
				// dismisses it here. Sending the cancel would turn a dead card into a transport error on
				// any auth or network hiccup, and a continuation would offer the step again.
				if (!decision.approved) {
					const holder = messagesRef.current[indexOfActionMessage(messagesRef.current, actionId)];
					if (holder && actionStateOf(holder, actionId).result?.status === 'expired') {
						setMessages((prev: Message[]) => dismissAction(prev, actionId));
						return {status: 'expired', guard: 'expired', message: 'This plan is no longer open.'};
					}
				}

				// Client-side expires_at pre-flight, for approvals only. The server returns the
				// 'expired' status when MCP rejects on TTL miss; this just avoids the round-trip
				// when we already know it's gone. A rejection always reaches the gateway.
				const expiresMs = decision.approved && plan.expires_at ? Date.parse(plan.expires_at) : NaN;
				if (Number.isFinite(expiresMs) && expiresMs < Date.now()) {
					result = {
						status: 'expired',
						guard: 'expired',
						message: 'This plan has expired. Please ask Nova for a fresh plan.',
					};
				} else {
					// Compute exclude_ids = (all affected − user's selection). Use each
					// item's originalId (the pre-dedup server id) rather than the React-key
					// `id`, which parseActionPlan suffixes on collisions ('10234-2'). Parse
					// strictly: a whole-number id becomes an int; a non-numeric id is
					// dropped + logged rather than silently partial-parsed (Number.parseInt
					// turned '12abc' into 12 and '10234-2' into 10234). De-dup so genuinely
					// duplicated entities don't emit the same int twice.
					const selectedSet = new Set(decision.selectedIds);
					const excludeSet = new Set<number>();
					if (decision.approved) {
						for (const item of plan.affected) {
							if (selectedSet.has(item.id)) continue;
							const serverId = item.originalId ?? item.id;
							if (/^\d+$/.test(serverId)) {
								excludeSet.add(Number(serverId));
							} else {
								console.warn('[respondToAction] non-numeric affected id, cannot build exclude_ids', serverId);
							}
						}
					}
					const excludeInts = [...excludeSet];

					const threadId = agentRef.current?.threadId ?? '';
					const req: ResolveRequest = {
						actionId,
						approved: decision.approved,
						entity_type: plan.entity_type,
						entity_subtype: plan.entity_subtype,
						exclude_ids: excludeInts,
						thread_id: threadId,
						overrides: decision.overrides,
						edited_values: decision.editedValues,
						...(!decision.approved && decision.retriedCancel ? {retriedCancel: true} : {}),
					};

					// Use a caller-provided transport if one was passed (tests
					// stub it for unit coverage). Otherwise fall back to the
					// default fetch() transport that POSTs the same gateway as
					// the SSE stream with the auth headers the HttpAgent uses.
					if (resolveActionRef.current) {
						result = await resolveActionRef.current(req);
					} else {
						result = await defaultResolveActionTransport(req, {
							apiKey: prevApiKeyRef.current ?? '',
							viewerPersonId: viewerPersonIdRef.current,
							companyId: companyIdRef.current,
							companyName: companyNameRef.current,
						});
					}
				}
			} catch (e) {
				result = {status: 'failed', guard: 'transport', message: e instanceof Error ? e.message : 'failed'};
			}
			// A rejection the gateway never received recorded nothing, so the card stays open and
			// nothing is saved for it: a restored tab shows a live card, not a stale outcome.
			if (!decision.approved && isTransportFailure(result)) return result;
			// Persist the resolution. A rejection (approved:false) also marks the
			// message dismissed so a rehydrated cancelled card stays "Cancelled."
			// instead of restoring as Failed+Retry from the gateway's result.
			setMessages((prev: Message[]) => {
				const applied = applyActionResult(prev, actionId, result);
				// An unresolved cancel stays undismissed: the plan may have been applied, and a
				// dismissed card no longer counts as one that did.
				return decision.approved || result.status === 'unresolved' ? applied : dismissAction(applied, actionId);
			});
			if (shouldContinueAfter(result, decision.approved) && agentRef.current === agentAtConfirm) {
				scheduleContinuation();
			}
			return result;
		},
		[]
	);

	// ── Report-intent candidate pick ─────────────────────────────────────────
	// The card's payload is sent whole (the gateway keeps no copy of the draft) against the same
	// LangGraph thread the SSE stream runs on, so the next turn on that thread picks the patched
	// draft up. Returns the result for the caller to apply; it never touches `messages` itself.
	const applyReportPick = useCallback(
		async (payload: Record<string, unknown>, term: string, candidate: string): Promise<ApplyReportPickResult> => {
			const req: ApplyReportPickRequest = {thread_id: agentRef.current?.threadId ?? '', payload, term, candidate};
			try {
				if (applyReportPickTransportRef.current) {
					return await applyReportPickTransportRef.current(req);
				}
				return await defaultApplyReportPickTransport(req, {
					apiKey: prevApiKeyRef.current ?? '',
					viewerPersonId: viewerPersonIdRef.current,
					companyId: companyIdRef.current,
					companyName: companyNameRef.current,
				});
			} catch (e) {
				return {status: 'failed', message: e instanceof Error ? e.message : 'failed'};
			}
		},
		[]
	);

	// ── Send ──────────────────────────────────────────────────────────────────
	const sendMessage = useCallback(
		async (userText: string, allMessages: Message[], opts: {hidden?: boolean} = {}): Promise<void> => {
			if (isStreaming) return;
			// A turn the user sends carries any pending confirm outcome itself, so a follow-up
			// queued for that outcome is no longer needed.
			if (!opts.hidden) cancelContinuation();
			// An empty send is allowed, but there is no text to show, so it is not rendered either.
			const hidden = opts.hidden === true || userText.trim().length === 0;

			setIsStreaming(true);
			resetSteps();
			// Reset so a turn whose fetch never resolves can't inherit a stale id
			// from an earlier, unrelated turn.
			correlationIdRef.current = null;

			const sentAt = Date.now();
			const turnNumber = allMessages.filter((m: Message) => m.role === 'user' && !m.hidden).length + (hidden ? 0 : 1);
			// The reply and any proposal belong to the drawer session this turn was sent in, even if
			// they land after the drawer was closed or reopened.
			const turnSessionId = getNovaIdentity().drawerSessionId;
			let planAttached = false;
			// Cards already proposed in this run, so a repeated action_id (which attachActionPlan drops) is not counted twice.
			const proposedActionIds = new Set<string>();

			const assistantMsgId = uuid();
			// The text dropped by the most recent tool-call preamble wipe below. A turn
			// that ends in an action card produces no answer after its tools, so that
			// wipe would otherwise be the last word on whatever the model had said —
			// including a refusal to skip approval, or a caveat about the write. Held
			// here so the card can put it back.
			let droppedPreamble = '';
			// True between restoring a dropped preamble onto a card-bearing message and the first
			// text the model streams afterwards. The restored text is a stand-in for an answer that
			// never came; real post-card text replaces it rather than being appended to it.
			let preambleRestored = false;
			setMessages((prev: Message[]) => [
				...prev,
				{
					id: uuid(),
					role: 'user',
					content: userText,
					createdAt: new Date().toISOString(),
					...(hidden ? {hidden: true} : {}),
				},
				{
					id: assistantMsgId,
					role: 'assistant',
					content: '',
					pending: true,
					statusSteps: [],
					createdAt: new Date().toISOString(),
				},
			]);

			let thinkingId: string | null = null;
			const toolCallStepMap = new Map<string, string>(); // toolCallId → stepId
			const sentinelToolCallMap = new Map<string, string>(); // toolCallId → toolName (for sentinel tools)
			const approvalToolCallIds = new Set<string>(); // toolCallIds for user_approval_tool (no visible step)
			const toolCallNameMap = new Map<string, string>(); // toolCallId → toolName (for Relay store invalidation after mutations)
			const toolCallArgsMap = new Map<string, Record<string, unknown>>(); // toolCallId → toolCallArgs (for extracting IDs on mutation results)
			let textStarted = false;
			// Set at each TEXT_MESSAGE_START: the first delta of a later message must not be glued onto the
			// previous message's text (the segments around a tool call arrive as separate messages).
			let newTextSegment = false;
			// Index in the message content where the current text segment begins. A visible tool call
			// wipes only from here, so a completed earlier segment survives.
			let segmentStart = 0;
			let finished = false;

			const finish = (patch: Partial<Message> = {}): void => {
				if (finished) return;
				finished = true;
				if (patch.error !== 'stopped') {
					if (patch.error) {
						trackNovaChatReplyFailed({turnNumber}, turnSessionId);
					} else {
						trackNovaChatReplyReceived(
							{
								latencyMs: Date.now() - sentAt,
								responseType: planAttached ? NOVA_RESPONSE_TYPE.ACTION_BOX : NOVA_RESPONSE_TYPE.TEXT,
								// Only rendered steps: sentinels and the approval tool never push one.
								toolCallCount: stepsRef.current.filter((st: StatusStep) => st.stepType === 'tool').length,
							},
							turnSessionId
						);
					}
				}
				doneAll();
				syncSteps(assistantMsgId);
				setIsHitlPending(false);
				setMessages((prev: Message[]) =>
					prev.map((m: Message) => (m.id === assistantMsgId ? {...m, pending: false, ...patch} : m))
				);
				setIsStreaming(false);
			};

			const agent = agentRef.current;
			if (!agent) {
				finish({error: 'Agent not initialised'});
				return;
			}

			agent.setMessages([
				...allMessages
					.filter((m: Message) => !m.pending)
					.map((m: Message) => ({id: m.id, role: m.role, content: m.content})),
				{id: uuid(), role: 'user', content: userText},
			] as Parameters<HttpAgent['setMessages']>[0]);

			const subscriber: AIChatSubscriber = {
				onStepStartedEvent({event}: {event: StepEvt}) {
					const stepName = event && event.stepName;
					if (stepName === 'call_model' && !thinkingId) {
						thinkingId = pushStep({stepType: 'thinking'});
						syncSteps(assistantMsgId);
					}
				},
				onStepFinishedEvent({event}: {event: StepEvt}) {
					const stepName = event && event.stepName;
					if (stepName === 'call_model' && thinkingId) {
						updateStep(thinkingId, {done: true});
						thinkingId = null;
						syncSteps(assistantMsgId);
					}
				},
				onToolCallStartEvent({event}: {event: ToolCallStartEvt}) {
					if (event.toolCallName === USER_APPROVAL_TOOL) {
						setIsHitlPending(true);
						approvalToolCallIds.add(event.toolCallId);
						return;
					}
					if (SENTINEL_TOOLS.has(event.toolCallName)) {
						sentinelToolCallMap.set(event.toolCallId, event.toolCallName);
						return;
					}
					// Drop any preamble the model streamed before deciding to call a
					// tool (e.g. "I'll fetch the health of all running projects"). The
					// real answer is produced after tools return; without this the
					// preamble stays concatenated in front of the final answer and
					// persists in the bubble.
					// Text after the wipe starts a new segment, so it is separated from what was kept.
					newTextSegment = true;
					setMessages((prev: Message[]) =>
						prev.map((m: Message) => {
							if (m.id !== assistantMsgId) return m;
							// While the text restored onto a card is still standing, wiping it again
							// would blank it and overwrite droppedPreamble with it, and nothing runs a
							// second time to put it back. Once the model streams text after a card, that
							// text is a new preamble and is wiped like any other before the next tool.
							if (m.actionPlans?.length && preambleRestored) return m;
							const current = m.content.slice(segmentStart);
							if (current.trim().length > 0) droppedPreamble = current;
							const kept = m.content.slice(0, segmentStart).trimEnd();
							segmentStart = kept.length;
							return {...m, content: kept};
						})
					);
					if (thinkingId) {
						updateStep(thinkingId, {done: true});
						thinkingId = null;
					}
					const toolName = event.toolCallName;
					const stepId = pushStep({stepType: 'tool', toolName});
					toolCallStepMap.set(event.toolCallId, stepId);
					toolCallNameMap.set(event.toolCallId, toolName);
					syncSteps(assistantMsgId);
				},
				onToolCallEndEvent({event, toolCallArgs}: {event: ToolCallEndEvt; toolCallArgs: Record<string, unknown>}) {
					// Flag the answer as cut off. Handled here rather than with the other
					// sentinels because the gateway emits no TOOL_CALL_RESULT for this one,
					// so onToolCallResultEvent never fires for it.
					if (sentinelToolCallMap.get(event.toolCallId) === SentinelTool.ResponseTruncated) {
						sentinelToolCallMap.delete(event.toolCallId);
						setMessages((prev: Message[]) =>
							prev.map((m: Message) => (m.id === assistantMsgId ? {...m, truncated: true} : m))
						);
						return;
					}
					if (approvalToolCallIds.has(event.toolCallId)) {
						const plan = parseActionPlan(toolCallArgs);
						if (plan) {
							planAttached = true;
							if (!proposedActionIds.has(plan.action_id)) {
								proposedActionIds.add(plan.action_id);
								trackNovaActionProposed(plan, turnSessionId);
							}
						}
						// Restore what the preamble wipe took, unless the model wrote something
						// after its tools. This runs whether or not the payload parsed: an
						// unparseable one drops the card, and leaving the wipe standing on that
						// branch renders a visible empty bubble with the preamble gone for good.
						//
						// droppedPreamble is read inside the updater, not above it: the wipe
						// assigns it from inside its own updater, and React defers both, so a
						// read out here would run before the wipe had written anything.
						setMessages((prev: Message[]) =>
							prev.map((m: Message) => {
								if (m.id !== assistantMsgId) return m;
								const keptContent = m.content.trim().length > 0;
								const restored = droppedPreamble;
								droppedPreamble = '';
								if (!keptContent && restored) preambleRestored = true;
								const withPlan = plan ? attachActionPlan(m, plan) : m;
								return {...withPlan, content: keptContent ? m.content : restored};
							})
						);
						return;
					}
					const stepId = toolCallStepMap.get(event.toolCallId);
					if (stepId) {
						updateStep(stepId, {input: toolCallArgs});
						syncSteps(assistantMsgId);
					}
					toolCallArgsMap.set(event.toolCallId, toolCallArgs);
				},
				onToolCallResultEvent({event}: {event: ToolCallResultEvt}) {
					const sentinelName = sentinelToolCallMap.get(event.toolCallId);
					if (sentinelName) {
						sentinelToolCallMap.delete(event.toolCallId);
						if (sentinelName === SentinelTool.CreateProjectIntent && onProjectIntentRef.current) {
							try {
								const data = JSON.parse(event.content) as Record<string, unknown>;
								onProjectIntentRef.current(data);
							} catch {
								// ignore parse errors — intent still fired, modal opens without prefill
							}
						}
						if (sentinelName === SentinelTool.CreateReportIntent && onReportIntentRef.current) {
							try {
								const data = JSON.parse(event.content) as Record<string, unknown>;
								onReportIntentRef.current(data);
							} catch {
								// ignore parse errors
							}
						}
						return;
					}
					const stepId = toolCallStepMap.get(event.toolCallId);
					if (stepId) {
						const content = event.content;
						updateStep(stepId, {
							done: true,
							output: content,
							outputTruncated: content.includes('[... output truncated at'),
						});
						toolCallStepMap.delete(event.toolCallId);
						syncSteps(assistantMsgId);
					}
					const mutatingToolName = toolCallNameMap.get(event.toolCallId);
					const mutatingToolArgs = toolCallArgsMap.get(event.toolCallId);
					toolCallNameMap.delete(event.toolCallId);
					toolCallArgsMap.delete(event.toolCallId);
					if (mutatingToolName === 'delete_project') {
						const rawFallback = mutatingToolArgs?.project_id;
						const result = applyDeleteProjectToRelayStore(
							event.content,
							typeof rawFallback === 'string' ? rawFallback : undefined
						);
						if (result) {
							onProjectDeletedRef.current?.(result.relayNodeId, result.customProjectId);
						}
					}
				},
				onTextMessageStartEvent() {
					newTextSegment = true;
					doneAll();
					const colId = pushStep({stepType: 'collating'});
					updateStep(colId, {done: true});
					syncSteps(assistantMsgId);
				},
				onTextMessageContentEvent({event}: {event: TextMessageContentEvt}) {
					if (!textStarted) {
						textStarted = true;
						if (!stepsRef.current.some((s: StatusStep) => s.stepType === 'collating')) {
							doneAll();
							const colId = pushStep({stepType: 'collating'});
							updateStep(colId, {done: true});
							syncSteps(assistantMsgId);
						}
					}
					const delta = event.delta || '';
					const startsSegment = newTextSegment && delta !== '';
					if (startsSegment) newTextSegment = false;
					// Text arriving after a restored preamble is the real answer. Appending would
					// splice it onto the end of the preamble with no separator, which is the
					// concatenation the wipe exists to prevent. Read inside the updater for the
					// same reason the restore is: the flag is set from inside one.
					setMessages((prev: Message[]) =>
						prev.map((m: Message) => {
							if (m.id !== assistantMsgId) return m;
							const replacePreamble = preambleRestored;
							preambleRestored = false;
							// Text arriving after a truncation notice means the answer did not
							// end at the cap after all, so the notice is stale and is dropped.
							// The gateway only signals on a turn-ending generation, so this
							// should not fire — it is here so a signal that escapes that guard
							// cannot leave a permanent "cut short" note on a complete answer,
							// which sessionStorage would then persist across a reload.
							const rest = {...m};
							delete rest.truncated;
							// A new text message after earlier text gets a paragraph break, unless that text
							// already ends in whitespace.
							const separator =
								startsSegment && !replacePreamble && m.content && !/\s$/.test(m.content) ? '\n\n' : '';
							if (replacePreamble) segmentStart = 0;
							else if (startsSegment) segmentStart = m.content.length + separator.length;
							return {
								...rest,
								content: replacePreamble ? delta : m.content + separator + delta,
								pending: false,
							};
						})
					);
				},
				onRunFinishedEvent() {
					finish();
				},
				onRunErrorEvent({event}: {event: RunErrorEvt}) {
					if (isAbortEvent(event)) {
						finish({error: 'stopped'});
						return;
					}
					finish({error: withCorrelationRef(event.message || 'An error occurred', correlationIdRef.current)});
				},
				onRunFailed({error}: {error: Error}) {
					if (error.name === 'AbortError') {
						finish({error: 'stopped'});
						return;
					}
					// Patches `content`, not `error`, so the bubble renders as one of
					// Nova's own replies rather than in the error styling.
					const refusal = gatewayRefusalMessage(error);
					if (refusal) {
						finish({content: refusal});
						return;
					}
					finish({error: withCorrelationRef(error.message || 'Connection error', correlationIdRef.current)});
				},
			};

			try {
				await agent.runAgent(
					{forwardedProps: {agent_id: FORECAST_CHAT_AGENT_ID}},
					subscriber as Parameters<HttpAgent['runAgent']>[1]
				);
			} catch (err) {
				if (err instanceof Error && err.name === 'AbortError') {
					finish({error: 'stopped'});
				} else {
					// Same treatment as the subscriber's onRunFailed: an immediate
					// non-ok response can surface here instead, depending on where
					// the library rejects, and a refusal must read the same either way.
					const refusal = gatewayRefusalMessage(err);
					if (refusal) {
						finish({content: refusal});
					} else {
						finish({
							error: withCorrelationRef(
								err instanceof Error ? err.message : 'Connection error',
								correlationIdRef.current
							),
						});
					}
				}
			} finally {
				setIsStreaming(false);
			}
		},
		[isStreaming, apiKey]
	);
	sendMessageRef.current = sendMessage;

	return {
		messages,
		setMessages,
		isStreaming,
		isHitlPending,
		sendMessage,
		abort,
		clearMessages,
		resetThread,
		respondToAction,
		applyReportPick,
	};
}
